import { gql, useApolloClient, useLazyQuery } from '@apollo/client';
import { EditorPlugin } from '@draft-js-plugins/editor';
import { MentionData } from '@draft-js-plugins/mention/lib/';
import { MentionSuggestionsPubProps } from '@draft-js-plugins/mention/lib/MentionSuggestions/MentionSuggestions';
import '@draft-js-plugins/mention/lib/plugin.css';
import { EditorState } from 'draft-js';
import * as React from 'react';

import { usePermissions } from '@/hooks/usePermissions';
import { formatDisplayName } from '@/utilities';

import { createEditorMentionPlugin } from '../../components/MentionSuggestionsWrapper/MentionSuggestionsWrapper';
import { Entry } from './components/Entry';
import {
  useMentions_mentionUserSearch,
  useMentions_mentionUserSearchVariables,
} from './graphql-types/useMentions_mentionUserSearch';

const MENTION_USER_SEARCH = gql`
  query useMentions_mentionUserSearch($input: MentionUserSearchInput!) {
    mentionUserSearch(input: $input) {
      results {
        userId
        firstName
        lastName
        fullName
        profilePictureUrl
        jobTitle
        company
        nameTitle
      }
    }
  }
`;

const MENTION_SEARCH_LIMIT = 5;
const MENTION_SEARCH_REGEX = /(?:^|\s)@([^\n@]*)$/;

const getMentionSearchValue = (editorState: EditorState): string | null => {
  const selection = editorState.getSelection();
  if (!selection.isCollapsed()) {
    return null;
  }

  const block = editorState
    .getCurrentContent()
    .getBlockForKey(selection.getStartKey());
  const textBeforeCursor = block.getText().slice(0, selection.getStartOffset());
  const match = MENTION_SEARCH_REGEX.exec(textBeforeCursor);
  return match ? match[1] : null;
};

const mapMentionResults = (
  results: useMentions_mentionUserSearch['mentionUserSearch']['results']
): MentionData[] =>
  results.map((result) => ({
    id: result.userId,
    name:
      formatDisplayName({
        firstName: result.firstName,
        lastName: result.lastName,
        nameTitle: result.nameTitle,
        name: result.fullName ?? undefined,
      }) ||
      result.fullName ||
      '',
    userId: result.userId,
    link: `/members/${result.userId}`,
    avatar: result.profilePictureUrl ?? undefined,
    jobTitle: result.jobTitle ?? undefined,
    company: result.company ?? undefined,
    nameTitle: result.nameTitle ?? undefined,
  }));

export const useMentions = (): {
  currentUserHasPermission: boolean;
  mentionPlugin: EditorPlugin & {
    MentionSuggestions: React.ComponentType<MentionSuggestionsPubProps>;
  };
  mentionSuggestionsProps: MentionSuggestionsPubProps;
  syncMentionSearch: (editorState: EditorState) => void;
} => {
  const [suggestions, setSuggestions] = React.useState<MentionData[]>([]);
  const [open, onOpenChange] = React.useState(false);
  const lastSearchRef = React.useRef<string | null>(null);
  const mentionPlugin = React.useMemo(() => createEditorMentionPlugin(), []);
  const client = useApolloClient();
  const { canAccessCommunityAndAuthenticated } = usePermissions();
  const currentUserHasPermission = canAccessCommunityAndAuthenticated;

  const [searchMentions] = useLazyQuery<
    useMentions_mentionUserSearch,
    useMentions_mentionUserSearchVariables
  >(MENTION_USER_SEARCH, {
    fetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setSuggestions(mapMentionResults(data?.mentionUserSearch?.results ?? []));
    },
  });

  const onSearchChange = React.useCallback(
    (event: { value: string }): void => {
      if (!currentUserHasPermission) {
        return;
      }

      if (lastSearchRef.current === event.value) {
        return;
      }
      lastSearchRef.current = event.value;

      const variables: useMentions_mentionUserSearchVariables = {
        input: {
          searchText: event.value,
          limit: MENTION_SEARCH_LIMIT,
        },
      };

      try {
        const cached = client.readQuery<
          useMentions_mentionUserSearch,
          useMentions_mentionUserSearchVariables
        >({
          query: MENTION_USER_SEARCH,
          variables,
        });
        if (cached?.mentionUserSearch?.results) {
          setSuggestions(mapMentionResults(cached.mentionUserSearch.results));
        }
      } catch {
        // No cached result for this search yet.
      }

      void searchMentions({ variables });
    },
    [client, currentUserHasPermission, searchMentions]
  );

  const syncMentionSearch = React.useCallback(
    (editorState: EditorState): void => {
      if (!currentUserHasPermission) {
        lastSearchRef.current = null;
        onOpenChange(false);
        setSuggestions([]);
        return;
      }

      const searchValue = getMentionSearchValue(editorState);
      if (searchValue === null) {
        lastSearchRef.current = null;
        onOpenChange(false);
        setSuggestions([]);
        return;
      }

      onOpenChange(true);
      onSearchChange({ value: searchValue });
    },
    [currentUserHasPermission, onSearchChange]
  );

  return {
    currentUserHasPermission,
    mentionPlugin,
    mentionSuggestionsProps: {
      open,
      onOpenChange,
      suggestions,
      onSearchChange,
      entryComponent: Entry,
    },
    syncMentionSearch,
  };
};
